import head from 'lodash/head';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { CommonWorkflowService } from '@savgroup-front-common/core/api';
import { ActionRow } from '@savgroup-front-common/core/atoms/ActionRow';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Card } from '@savgroup-front-common/core/atoms/Cards';
import { Form } from '@savgroup-front-common/core/atoms/Form';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { Paragraph } from '@savgroup-front-common/core/atoms/Paragraph';
import { AdditionalInformation } from '@savgroup-front-common/core/components/AdditionalInformationWithReactHookForm';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { useUploadAttachmentsOnFile } from '@savgroup-front-common/core/hooks/useUploadAttachmentsOnFile';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';

import useGetSellerConfiguration from '../../../../../app/hooks/useGetSellerConfiguration';
import { useFileFollowupsContext } from '../../FileFollowups.context';

import { AdaptedNeededInformation } from './AdditionalInformationExpect.types';
import { useAdditionalInformationExpectContent } from './AdditionalInformationExpectContent.hooks';
import messages from './messages';
import ProductAdditionalInformation from './ProductAdditionalInformation';

interface AdditionalInformationExpectContentProps {
  additionalInformations: Record<string, AdaptedNeededInformation[]>;
}

const AdditionalInformationExpectContent: FC<
  AdditionalInformationExpectContentProps
> = ({ additionalInformations }) => {
  const { fileId } = useParams() as { fileId: string };
  const {
    formContext,
    handleAdditionalInformationNeededSubmit,
    singleProduct,
    headProduct,
    groupedProducts,
  } = useAdditionalInformationExpectContent({
    additionalInformations,
  });

  const {
    formState: { isSubmitting, errors, isSubmitSuccessful },
  } = formContext;
  const { fileSummary } = useFileFollowupsContext();

  const { handleUploadDocuments } = useUploadAttachmentsOnFile({
    fileId,
  });
  const { sellerConfiguration: seller } = useGetSellerConfiguration({
    sellerId: fileSummary?.sellerId,
  });

  if (!fileSummary) {
    return <BaseLoader />;
  }

  return (
    <Card>
      <div>
        <Heading level={2}>
          <SafeFormattedMessageWithoutSpread message={messages.header} />
        </Heading>
        <Paragraph>
          <SafeFormattedMessageWithoutSpread message={messages.subHeader} />
        </Paragraph>
      </div>

      <Form
        formContext={formContext}
        onSubmit={handleAdditionalInformationNeededSubmit}
      >
        {singleProduct && headProduct && headProduct.neededInformation && (
          <AdditionalInformation
            ignoreUniquenessByProduct
            formContext={formContext}
            neededInformation={headProduct.neededInformation as any}
            isNewDesign
            onNeedToUploadFileAttachements={handleUploadDocuments}
            sellerId={fileSummary.sellerId}
            getQrUploadLink={
              CommonWorkflowService.getFileAttachmentUploadLinkFromMyAccountCommand
            }
            fileReference={fileSummary.fileReference}
            currency={seller?.currency}
          />
        )}

        {!singleProduct &&
          groupedProducts.map((product) => {
            if (
              !product.neededInformation ||
              product.neededInformation.length === 0
            ) {
              return null;
            }

            return (
              <ProductAdditionalInformation
                key={head(product.ownerProductIds)}
                product={product}
              />
            );
          })}

        <ActionRow>
          <Button
            primary
            type={BUTTON_TYPES.SUBMIT}
            isLoading={isSubmitting || isSubmitSuccessful}
            isError={Object.values(errors).length > 0}
          >
            <SafeFormattedMessageWithoutSpread message={messages.submit} />
          </Button>
        </ActionRow>
      </Form>
    </Card>
  );
};

export default AdditionalInformationExpectContent;
